import * as React from 'react';
import { Fragment, useState, useEffect } from 'react';

/** Material UI Packages. */
import { Toolbar, List, Tooltip, ListItemButton, Typography } from '@mui/material';

/** Material UI Icons. */
import IconButton from '@mui/material/IconButton';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import LogoutIcon from '@mui/icons-material/Logout';

/** Client-side routing Library.  */
import { Link, useLocation } from 'react-router-dom';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import insight_logo_horizontal from 'statics/images/insight_logo_horizontal.png';
import { customized_style } from 'shared/config/Styles';
import SubMenu from './SubMenu';
import { isManagerOrAbove, showsAssistantManagerMenu } from 'shared/lib/roles';

export default function Menubar(props) {
    /** Get props from RenderComponent.js.  */
    const { userRole } = props;
    /** Auth0 authenticator. */
    const { logout } = useAuth0();

    /** Get the current location. */
    const location = useLocation();

    /** State for endpoint value of selected Menu list.  */
    const [selectedItem, setSelectedItem] = useState(null);
    /** State to manage endpoint when click the logo image. */
    const [homeValue, setHomeValue] = useState('');
    /** State to manage the currently opened menu (for submenu). */
    const [anchorEl, setAnchorEl] = useState(null);
    /** State to store the type of menu that is currently open (operation, reports, management). */
    const [menuType, setMenuType] = useState(null);

    /** Boolean to track if submenu is open based on the `anchorEl` state. */
    const open = Boolean(anchorEl);

    /** Handle to Set the anchor element and the type of menu being opened. */
    const handleClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setMenuType(type);
    };

    /** Resets the anchor element and clears the menu type state. */
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuType(null);
    };

    /** Handler to update selected Value.  */
    const handleItemClick = (item) => {
        setSelectedItem(item);
    };

    /** Handle for changing homeValue and selectedItem when you click a home image. */
    const handleHomeClick = () => {
        if (
            userRole.some((role) =>
                ['Operation-Manager', 'Operation-Assistant Manager', 'Operation-Staff'].includes(role)
            )
        ) {
            setHomeValue('/opdashboard');
            setSelectedItem('opdashboard');
        } else if (userRole.some((role) => role === 'Administrator')) {
            setHomeValue('/useractivity');
            setSelectedItem('useractivity');
        } else {
            setHomeValue('/');
        }
    };

    /** Initiallize menu selection based on userRole. */
    useEffect(() => {
        const location_path = location.pathname.slice(1);
        let selectedKey = null;
        if (location_path === 'myprofile') {
            selectedKey = 'myprofile';
        } else if (location_path === 'accounting') {
            selectedKey = 'accounting';
        } else {
            // Iterate through linkData to check its values.
            for (const [key, values] of Object.entries(linkData)) {
                if (values.includes(location_path)) {
                    selectedKey = key;
                    break;
                }
            }
        }

        // Update selectedItem if a value is selected; otherwise, set it to the default 'opdashboard'.
        setSelectedItem(selectedKey || 'opdashboard');
    }, [location]);

    /** Menu data object containing items for each menu section. */
    const menuData = {
        operation: ['Shipments', 'Data Entry', 'Duty Calculator', 'Quote Archive', 'Guides & FAQs'],
        reports: ['Analytics', 'Data Export'],
        pricing: ['Rate Management'],
        management: ['Shipment Monitoring', 'Task Board', 'Client Setting', 'Performance'],
        management_semi: ['Task Board'],
    };

    const linkData = {
        operation: ['shipments', 'dataentry', 'dutycalculator', 'quotearchive', 'guideline'],
        reports: ['analytics', 'dataexport'],
        pricing: ['ratemanagement'],
        management: ['shipmentmonitoring', 'taskboard', 'clientSetting', 'performance'],
        management_semi: ['taskboard'],
        useractivity: ['useractivity'],
    };

    return (
        <Fragment>
            <Toolbar
                sx={{
                    bgcolor: 'white',
                    color: '#00657e',
                    minHeight: 48,
                }}
                variant="dense"
            >
                <Link to={homeValue} style={{ cursor: 'pointer' }} onClick={handleHomeClick}>
                    <img
                        src={insight_logo_horizontal}
                        alt="Insight Logo"
                        style={{
                            height: '48px',
                            marginRight: 'auto',
                            marginLeft: 0,
                        }}
                    />
                </Link>
                <Typography sx={{ flexGrow: 1 }}></Typography>
                <List sx={{ display: 'flex', flexDirection: 'row', padding: 0 }}>
                    <Fragment>
                        {userRole.some((role) =>
                            [
                                'Operation-Staff',
                                'Operation-Assistant Manager',
                                'Operation-Manager',
                                'Administrator',
                            ].includes(role)
                        ) && (
                            <>
                                <ListItemButton
                                    sx={customized_style.itemMenu}
                                    component={Link}
                                    to="/opdashboard"
                                    selected={selectedItem === 'opdashboard'}
                                    onClick={() => handleItemClick('opdashboard')}
                                >
                                    DASHBOARD
                                </ListItemButton>
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'operation')}
                                    selected={selectedItem === 'operation'}
                                    sx={customized_style.itemMenu}
                                >
                                    OPERATION
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'operation'}
                                    handleClose={handleMenuClose}
                                    menuItems={menuData.operation}
                                    linkItems={linkData.operation}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'operation'}
                                />
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'reports')}
                                    selected={selectedItem === 'reports'}
                                    sx={customized_style.itemMenu}
                                >
                                    REPORTS
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'reports'}
                                    handleClose={handleMenuClose}
                                    menuItems={menuData.reports}
                                    linkItems={linkData.reports}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'reports'}
                                />
                                <ListItemButton
                                    sx={customized_style.itemMenu}
                                    component={Link}
                                    to="/accounting"
                                    selected={selectedItem === 'accounting'}
                                    onClick={() => handleItemClick('accounting')}
                                >
                                    ACCOUNTING
                                </ListItemButton>
                            </>
                        )}
                        {showsAssistantManagerMenu(userRole) && (
                            <>
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'pricing')}
                                    selected={selectedItem === 'pricing'}
                                    sx={customized_style.itemMenu}
                                >
                                    PRICING
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'pricing'}
                                    handleClose={handleMenuClose}
                                    menuItems={menuData.pricing}
                                    linkItems={linkData.pricing}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'pricing'}
                                />
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'management_semi')}
                                    selected={selectedItem === 'management_semi'}
                                    sx={customized_style.itemMenu}
                                >
                                    MANAGEMENT
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'management_semi'}
                                    handleClose={handleMenuClose}
                                    menuItems={menuData.management_semi}
                                    linkItems={linkData.management_semi}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'management_semi'}
                                />
                            </>
                        )}
                        {isManagerOrAbove(userRole) && (
                            <>
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'pricing')}
                                    selected={selectedItem === 'pricing'}
                                    sx={customized_style.itemMenu}
                                >
                                    PRICING
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'pricing'}
                                    handleClose={handleMenuClose}
                                    menuItems={menuData.pricing}
                                    linkItems={linkData.pricing}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'pricing'}
                                />
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'management')}
                                    selected={selectedItem === 'management'}
                                    sx={customized_style.itemMenu}
                                >
                                    MANAGEMENT
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'management'}
                                    handleClose={handleMenuClose}
                                    menuItems={menuData.management}
                                    linkItems={linkData.management}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'management'}
                                />
                            </>
                        )}
                        {userRole.some((role) => ['Administrator'].includes(role)) && (
                            <>
                                <ListItemButton
                                    sx={customized_style.itemMenu}
                                    component={Link}
                                    to="/useractivity"
                                    selected={selectedItem === 'useractivity'}
                                    onClick={() => handleItemClick('useractivity')}
                                >
                                    USER ACTIVITY
                                </ListItemButton>
                            </>
                        )}
                    </Fragment>
                </List>
                <Tooltip title="My Profile">
                    <IconButton color="inherit" component={Link} to="/myprofile">
                        <InsertEmoticonIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="LogOut">
                    <IconButton
                        color="inherit"
                        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    >
                        <LogoutIcon />
                    </IconButton>
                </Tooltip>
            </Toolbar>
        </Fragment>
    );
}
